import { useEffect, useState } from 'react';
import { loadGithubBase } from 'utils/githubSettingsCache';

export interface GithubConnectionState {
    /** False until the check resolves — render controls as "loading", not as "disconnected". */
    ready: boolean;
    connected: boolean;
}

/**
 * Whether the signed-in user has GitHub connected.
 *
 * Reads through githubSettingsCache's module-level memo, so this costs a round-trip once per page
 * session no matter how many components ask — opening Settings and landing on the Plan page share
 * the same answer. That memo is also what `resetGithubSessionCache()` clears on disconnect.
 *
 * Deliberately does NOT report which repo is selected: that is a per-project preference and lives
 * in the store (`githubRepo`), so a component needing both reads them from their own sources.
 */
export default function useGithubConnection(): GithubConnectionState {
    const [state, setState] = useState<GithubConnectionState>({ ready: false, connected: false });

    useEffect(() => {
        let mounted = true;

        loadGithubBase()
            .then((base) => {
                if (mounted) setState({ ready: true, connected: base.githubConnected });
            })
            .catch(() => {
                // A failed check reads as "not connected" rather than leaving the control stuck
                // in a loading state forever — the user can still fix it in Settings.
                if (mounted) setState({ ready: true, connected: false });
            });

        return () => {
            mounted = false;
        };
    }, []);

    return state;
}
